import { useState, type FormEvent } from "react";
import {
  activateSubscription,
  getContract,
  issueContract,
  listContracts,
  listContractTemplates,
  renderContractTemplate,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate, formatMoney } from "../lib/format";
import type {
  ContractDetailResponse,
  IssueContractResult,
  PlanRow,
} from "../lib/types";
import { ContractBadge, SignerBadge } from "./Badge";
import { CopyButton } from "./CopyButton";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "./States";
import { Modal } from "./Modal";

interface SignerDraft {
  name: string;
  email: string;
}

/** Default body for a 90-day, no-payment trial agreement. */
const TRIAL_BODY_HTML = `<h1>90-Day Trial Agreement</h1>
<p>This Trial Agreement is entered into between <strong>ActionConnect</strong> and the undersigned institution ("Customer").</p>
<h2>Trial term</h2>
<p>ActionConnect grants Customer access to an isolated ActionConnect workspace for a <strong>90-day evaluation period</strong> at <strong>no charge</strong>. No payment is due during the trial and no card is required.</p>
<h2>After the trial</h2>
<p>At the end of the trial, Customer may sign a paid order form to continue, or the workspace will be paused. Customer data is retained for 30 days after the trial ends, then deleted.</p>
<p>By signing below, the undersigned agrees to evaluate ActionConnect under these terms.</p>`;

export function ContractsPanel({ tenantId, plans }: { tenantId: string; plans: PlanRow[] }) {
  const { data, loading, error, reload } = useApi(
    (signal) => listContracts(tenantId, signal),
    [tenantId],
  );
  const contracts = data?.contracts ?? [];

  const [showIssue, setShowIssue] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [viewContractId, setViewContractId] = useState<string | null>(null);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Contracts &amp; e-signatures</h2>
        <div className="btn-row">
          <button className="btn small" onClick={() => setShowActivate(true)}>
            Activate subscription
          </button>
          <button className="btn primary small" onClick={() => setShowIssue(true)}>
            + Issue contract
          </button>
        </div>
      </div>
      <div className="card-body tight">
        {loading ? (
          <Loading label="Loading contracts…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : contracts.length === 0 ? (
          <EmptyState>No contracts issued for this tenant yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Term</th>
                <th>Amount</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.title}</strong>
                  </td>
                  <td>
                    <ContractBadge status={c.status} />
                  </td>
                  <td className="num">{c.term_months != null ? `${c.term_months} mo` : "—"}</td>
                  <td className="num">{formatMoney(c.amount_cents, c.currency)}</td>
                  <td className="num" style={{ whiteSpace: "nowrap" }}>
                    {formatDate(c.created_at)}
                  </td>
                  <td>
                    <button className="btn ghost small" onClick={() => setViewContractId(c.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showIssue ? (
        <IssueContractModal
          tenantId={tenantId}
          plans={plans}
          onClose={() => setShowIssue(false)}
          onIssued={() => reload()}
        />
      ) : null}

      {showActivate ? (
        <ActivateSubscriptionModal
          tenantId={tenantId}
          plans={plans}
          onClose={() => setShowActivate(false)}
        />
      ) : null}

      {viewContractId ? (
        <ContractDetailModal cid={viewContractId} onClose={() => setViewContractId(null)} />
      ) : null}
    </div>
  );
}

function IssueContractModal({
  tenantId,
  plans,
  onClose,
  onIssued,
}: {
  tenantId: string;
  plans: PlanRow[];
  onClose: () => void;
  onIssued: () => void;
}) {
  const [title, setTitle] = useState("");
  const [bodyHtml, setBodyHtml] = useState(
    "<h1>Service Agreement</h1>\n<p>This agreement is entered into between ActionConnect and the undersigned…</p>",
  );
  const [planCode, setPlanCode] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [amount, setAmount] = useState("");
  const [signers, setSigners] = useState<SignerDraft[]>([{ name: "", email: "" }]);
  const [result, setResult] = useState<IssueContractResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Reusable templates: picking one renders its markdown (with this tenant's
  // placeholders) into the editable title + body below.
  const templatesApi = useApi((signal) => listContractTemplates(signal), []);
  const templates = templatesApi.data?.templates ?? [];
  const [templateId, setTemplateId] = useState("");
  const [rendering, setRendering] = useState(false);
  // 90-day trial = a $0, no-payment agreement. Toggling it zeroes the amount,
  // sets a 3-month term, and seeds a trial body/title if the fields are empty.
  const [isTrial, setIsTrial] = useState(false);
  function toggleTrial(on: boolean) {
    setIsTrial(on);
    if (on) {
      setAmount("0");
      setTermMonths("3");
      setTitle((t) => t || "90-Day Trial Agreement");
      setBodyHtml((b) =>
        b && !b.startsWith("<h1>Service Agreement</h1>")
          ? b
          : TRIAL_BODY_HTML,
      );
    }
  }

  async function pickTemplate(id: string) {
    setTemplateId(id);
    if (!id) return;
    setRendering(true);
    try {
      const r = await renderContractTemplate(id, { tenantId });
      setTitle(r.title);
      setBodyHtml(r.bodyHtml);
    } catch {
      /* leave the current body; the select still reflects the choice */
    } finally {
      setRendering(false);
    }
  }

  const { run, pending, error } = useMutation(issueContract);

  function updateSigner(i: number, patch: Partial<SignerDraft>) {
    setSigners((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function addSigner() {
    setSigners((prev) => [...prev, { name: "", email: "" }]);
  }
  function removeSigner(i: number) {
    setSigners((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  }

  const validSigners = signers.filter((s) => s.name.trim() && s.email.trim());
  const canSubmit = title.trim() && bodyHtml.trim() && validSigners.length > 0;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const amountCents = amount.trim() ? Math.round(parseFloat(amount) * 100) : undefined;
    const term = termMonths.trim() ? parseInt(termMonths, 10) : undefined;
    const res = await run(tenantId, {
      title: title.trim(),
      bodyHtml,
      ...(templateId ? { templateId } : {}),
      ...(planCode ? { planCode } : {}),
      ...(term && !Number.isNaN(term) ? { termMonths: term } : {}),
      ...(amountCents != null && !Number.isNaN(amountCents) ? { amountCents } : {}),
      signers: validSigners.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
    });
    if (res) {
      setResult(res);
      onIssued();
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard may be blocked; the link is still selectable */
    }
  }

  if (result) {
    return (
      <Modal
        title="Contract issued"
        wide
        onClose={onClose}
        footer={
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <Alert kind="success">
          Contract <strong>{result.contract.title}</strong> created and sent.{" "}
          {result.signers.every((s) => s.emailed)
            ? "Each signer was emailed their signing link."
            : "Share each signing link with its signer — these tokens are shown once."}
        </Alert>
        {result.signers.map((s) => (
          <div key={s.signerId} className="signer-link">
            <span className="muted" style={{ minWidth: 0 }}>
              {s.email}
              {s.emailed ? <span className="pill ok" style={{ marginLeft: 6 }}>emailed</span> : null}
            </span>
            <span className="url">{s.signUrl}</span>
            <button type="button" className="btn small" onClick={() => copy(s.signUrl)}>
              {copied === s.signUrl ? "Copied" : "Copy"}
            </button>
          </div>
        ))}
      </Modal>
    );
  }

  return (
    <Modal
      title="Issue contract"
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="issue-contract-form"
            disabled={pending || !canSubmit}
          >
            {pending ? <Spinner label="Issuing…" /> : "Issue & send"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="issue-contract-form" onSubmit={onSubmit}>
        {templates.length > 0 ? (
          <label className="field">
            <span className="label">Start from template (optional)</span>
            <select value={templateId} onChange={(e) => pickTemplate(e.target.value)} disabled={rendering}>
              <option value="">Blank — write below</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.code})
                </option>
              ))}
            </select>
            <span className="hint">
              {rendering
                ? "Rendering template…"
                : "Fills the title & body with this tenant's details — edit freely before issuing."}
            </span>
          </label>
        ) : null}

        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Service Agreement — Acme Inc."
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span className="label">Body (HTML)</span>
          <textarea
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={7}
            required
          />
          <span className="hint">
            Snapshotted and hashed at issue time; signatures bind to this exact content.
          </span>
        </label>

        <label className="toggle" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={isTrial} onChange={(e) => toggleTrial(e.target.checked)} />
          90-day trial — no payment (zeroes the amount, sets a 3-month term)
        </label>

        <div className="row">
          <label className="field">
            <span className="label">Plan (optional)</span>
            <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
              <option value="">None</option>
              {plans.map((p) => (
                <option key={p.id} value={p.code}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Term (months)</span>
            <input
              type="number"
              min="1"
              value={termMonths}
              onChange={(e) => setTermMonths(e.target.value)}
              placeholder="12"
            />
          </label>
          <label className="field">
            <span className="label">Amount (USD)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="4800.00"
              disabled={isTrial}
            />
            {isTrial ? <span className="hint">No payment for the trial.</span> : null}
          </label>
        </div>

        <div className="field">
          <span className="label">Signers</span>
          {signers.map((s, i) => (
            <div className="row" key={i} style={{ marginBottom: 8, alignItems: "center" }}>
              <input
                type="text"
                value={s.name}
                onChange={(e) => updateSigner(i, { name: e.target.value })}
                placeholder="Full name"
                aria-label={`Signer ${i + 1} name`}
              />
              <input
                type="email"
                value={s.email}
                onChange={(e) => updateSigner(i, { email: e.target.value })}
                placeholder="email@company.com"
                aria-label={`Signer ${i + 1} email`}
              />
              <button
                type="button"
                className="btn ghost small"
                style={{ flex: "0 0 auto" }}
                onClick={() => removeSigner(i)}
                disabled={signers.length === 1}
                aria-label={`Remove signer ${i + 1}`}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="btn small" onClick={addSigner}>
            + Add signer
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ActivateSubscriptionModal({
  tenantId,
  plans,
  onClose,
}: {
  tenantId: string;
  plans: PlanRow[];
  onClose: () => void;
}) {
  const [planCode, setPlanCode] = useState("");
  const [seats, setSeats] = useState("");
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(false);
  const { run, pending, error } = useMutation(activateSubscription);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const seatsNum = seats.trim() ? parseInt(seats, 10) : undefined;
    const res = await run(tenantId, {
      ...(planCode ? { planCode } : {}),
      ...(seatsNum != null && !Number.isNaN(seatsNum) ? { seats: seatsNum } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
    if (res) setDone(true);
  }

  if (done) {
    return (
      <Modal
        title="Subscription activated"
        onClose={onClose}
        footer={
          <button className="btn primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <Alert kind="success">
          The tenant's subscription is now active (trial flag cleared). This action was audited.
        </Alert>
      </Modal>
    );
  }

  return (
    <Modal
      title="Activate subscription"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="activate-sub-form"
            disabled={pending}
          >
            {pending ? <Spinner label="Activating…" /> : "Activate"}
          </button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        Convert the trial to a paid subscription, e.g. after a contract is signed. All fields are
        optional — leave blank to keep the current plan/seats.
      </p>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="activate-sub-form" onSubmit={onSubmit}>
        <div className="row">
          <label className="field">
            <span className="label">Plan</span>
            <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
              <option value="">Keep current</option>
              {plans.map((p) => (
                <option key={p.id} value={p.code}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Seats</span>
            <input
              type="number"
              min="1"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              placeholder="Keep current"
            />
          </label>
        </div>
        <label className="field">
          <span className="label">Notes</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Signed MSA 2026-06-07"
          />
        </label>
      </form>
    </Modal>
  );
}

function ContractDetailModal({ cid, onClose }: { cid: string; onClose: () => void }) {
  const { data, loading, error } = useApi<ContractDetailResponse>(
    (signal) => getContract(cid, signal),
    [cid],
  );

  return (
    <Modal title="Contract" wide onClose={onClose}>
      {loading ? (
        <Loading label="Loading contract…" />
      ) : error ? (
        <ErrorState error={error} />
      ) : data ? (
        <>
          <div className="card-head" style={{ padding: "0 0 12px", border: "none" }}>
            <h3 style={{ margin: 0 }}>{data.contract.title}</h3>
            <ContractBadge status={data.contract.status} />
          </div>
          <dl className="kv" style={{ marginBottom: 16 }}>
            <dt>Contract ID</dt>
            <dd className="mono">
              {data.contract.id}
              <CopyButton value={data.contract.id} label="contract ID" />
            </dd>
            <dt>Term</dt>
            <dd>{data.contract.term_months != null ? `${data.contract.term_months} months` : "—"}</dd>
            <dt>Amount</dt>
            <dd>{formatMoney(data.contract.amount_cents, data.contract.currency)}</dd>
            <dt>Created by</dt>
            <dd>{data.contract.created_by ?? "—"}</dd>
            <dt>Sent</dt>
            <dd>{formatDate(data.contract.sent_at)}</dd>
            <dt>Signed</dt>
            <dd>{formatDate(data.contract.signed_at)}</dd>
            <dt>Expires</dt>
            <dd>{formatDate(data.contract.expires_at)}</dd>
            <dt>Document hash</dt>
            <dd className="mono faint" style={{ fontSize: "0.75rem" }}>
              {data.contract.document_hash}
            </dd>
          </dl>

          <h3>Signers</h3>
          {data.signers.length === 0 ? (
            <EmptyState>No signers.</EmptyState>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Signed</th>
                </tr>
              </thead>
              <tbody>
                {data.signers.map((s) => (
                  <tr key={s.id}>
                    <td className="num">{s.signing_order + 1}</td>
                    <td>{s.name}</td>
                    <td className="mono">{s.email}</td>
                    <td>
                      <SignerBadge status={s.status} />
                    </td>
                    <td className="num">{formatDate(s.signed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </Modal>
  );
}
